import React, { useState } from "react";
import { STAGES } from "./Scoreboard";
import { authApi } from "../../lib/api";

export const INTEREST = ["Hot", "Warm", "Cold"];
export const LP_TYPES = ["Accredited Individual", "HNW Individual", "Family Office", "Independent Sponsor",
  "Institutional", "Private Equity Fund", "Fund of Funds", "RIA / Wealth Manager", "Search Fund",
  "Strategic / Corporate", "Debt / Mezz Lender", "Other"];
export const ACCRED = ["Unverified", "Self-certified", "Verified", "Not accredited"];
export const DOCS = ["None", "NDA sent", "NDA signed", "Sub docs sent", "Sub docs signed", "Wired"];

const fmtIn = (n) => (n ? Math.round(n).toLocaleString("en-US") : "");
const parse = (s) => +String(s).replace(/[^\d]/g, "") || 0;

const Txt = ({ label, k, form, set, type = "text", ph = "" }) => (
  <div className="field"><label>{label}</label>
    <input className="inp" type={type} placeholder={ph} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`lead-${k}`} />
  </div>
);
const Sel = ({ label, k, form, set, opts }) => (
  <div className="field"><label>{label}</label>
    <select className="inp" value={form[k] || opts[0]} onChange={(e) => set(k, e.target.value)} data-testid={`lead-${k}`}>
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
const Money = ({ label, k, form, set }) => (
  <div className="field"><label>{label}</label>
    <div className="lead-money">
      <span className="cur">$</span>
      <input className="mono" inputMode="numeric" placeholder="0" value={fmtIn(form[k])} onChange={(e) => set(k, parse(e.target.value))} data-testid={`lead-${k}`} />
    </div>
  </div>
);
const Group = ({ title, children, cols = 3 }) => (
  <section className="lead-group">
    <div className="section-label">{title}</div>
    <div className="lead-grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>{children}</div>
  </section>
);

export const LeadModal = ({ open, initial, dealKey, dealName, onClose, onSave }) => {
  const [form, setForm] = useState(initial || {});
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);
  React.useEffect(() => { if (open) { setForm(initial || { stage: "Intro Call", interest: "Warm", type: "Accredited Individual" }); authApi.users().then(setUsers).catch(() => {}); } }, [open, initial]);
  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!(form.name || "").trim()) return;
    setBusy(true);
    try { await onSave({ ...form, dealKey: form.dealKey || dealKey, dealName: form.dealName || dealName }); onClose(); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card lead-modal" onClick={(e) => e.stopPropagation()} data-testid="lead-modal">
        <div className="modal-head">
          <div><div className="modal-title">{initial?.id ? "Edit LP" : "Add LP after meeting"}</div>
            <div className="modal-sub">{dealName ? `Tracked against ${dealName}` : "Firm-wide LP roster"}</div></div>
          <button className="btn" onClick={onClose} data-testid="lead-modal-close">Close</button>
        </div>

        <Group title="Who they are">
          <Txt label="Name" k="name" form={form} set={set} ph="Jane Doe" />
          <Txt label="Firm / family office" k="firm" form={form} set={set} />
          <Sel label="Investor type" k="type" form={form} set={set} opts={LP_TYPES} />
          <Txt label="Email" k="email" form={form} set={set} type="email" />
          <Txt label="Phone" k="phone" form={form} set={set} />
          <Txt label="Entity name (for docs)" k="entityName" form={form} set={set} />
        </Group>

        <Group title="Where they sit in the raise">
          <Sel label="Stage" k="stage" form={form} set={set} opts={STAGES} />
          <Sel label="Interest" k="interest" form={form} set={set} opts={INTEREST} />
          <div className="field"><label>Close probability override %</label>
            <input className="inp mono" inputMode="numeric" placeholder="auto from stage"
              value={form.probability ?? ""} onChange={(e) => set("probability", e.target.value === "" ? null : Math.max(0, Math.min(100, parse(e.target.value))))} data-testid="lead-probability" />
          </div>
          <Txt label="Class preference (A/B/C)" k="classPref" form={form} set={set} />
          <Txt label="Intro source" k="source" form={form} set={set} ph="Conference, LinkedIn…" />
          <Txt label="Referred by" k="referrer" form={form} set={set} />
        </Group>

        <Group title="Ticket size">
          <Money label="Amount we asked for" k="askAmount" form={form} set={set} />
          <Money label="Indicated / discussed" k="indicated" form={form} set={set} />
          <Money label="Committed" k="committed" form={form} set={set} />
          <Money label="Funded / wired" k="funded" form={form} set={set} />
          <Money label="Min ticket" k="minTicket" form={form} set={set} />
          <Money label="Max ticket" k="maxTicket" form={form} set={set} />
        </Group>

        <Group title="Compliance, docs & timing">
          <Sel label="Accreditation" k="accredited" form={form} set={set} opts={ACCRED} />
          <Sel label="Docs status" k="docsStatus" form={form} set={set} opts={DOCS} />
          <Txt label="Wire received" k="wireDate" form={form} set={set} type="date" />
          <div className="field"><label>Owner (who runs this)</label>
            <select className="inp" value={form.owner || ""} onChange={(e) => set("owner", e.target.value)} data-testid="lead-owner">
              <option value="">Unassigned</option>
              {users.map((u) => <option key={u.id} value={u.name || u.email}>{u.name || u.email}</option>)}
              {form.owner && !users.some((u) => (u.name || u.email) === form.owner) && <option value={form.owner}>{form.owner}</option>}
            </select>
          </div>
          <Txt label="Last contact" k="lastContact" form={form} set={set} type="date" />
          <Txt label="Next follow-up" k="nextFollowUp" form={form} set={set} type="date" />
        </Group>

        <section className="lead-group">
          <div className="section-label">Meeting notes</div>
          <textarea className="inp" rows={4} placeholder="What they said, what they need next, ticket appetite…"
            value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="lead-notes" />
        </section>

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy || !(form.name || "").trim()} data-testid="lead-save">{busy ? "Saving…" : initial?.id ? "Save changes" : "Add to pipeline"}</button>
        </div>
      </div>
    </div>
  );
};
